import React, { useEffect, useRef, useState } from "react";

import { TablesDetailsInput, useUpdateTableDetailsForUserMutation } from "gql/generated/types";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent, DataTableValue, DataTableValueArray } from "primereact/datatable";
import { Path } from "react-hook-form";
import { T } from "services/lang/LanguageService";
import { exportExcel, moveItem } from "utils/utils";
import useCustomColumns from "../hooks/useCustomColumns";
import useProjectContext from "../hooks/useProjectContext";
import useUserTableDetails from "../hooks/useUserTableDetails";
import { MdmTableName, TableColumn } from "../utils/types";
import { findObjectValue } from "../utils/utils";
import TableColumnSelection from "./TableColumnSelection";
import TableToolbar from "./TableToolbar";
import { fmtDatetime } from "utils/time-utils";
import { DATE_FORMATTER_MINUTES } from "utils/constants";
import { useTimezone } from "hooks/useTimezone";

type ItemsTableProps<T extends DataTableValue> = {
  columns: TableColumn<T>[];
  data: T[];
  searchFields?: Path<T>[];
  toolbarBtnClassName?: string;
  toolbarBtnLabel?: string;
  onToolbarBtnClick?: () => void;
  loading?: boolean;
  showToolbar?: boolean;
  showToolbarBtn?: boolean;
  tableName: MdmTableName;
  setFilteredData?: (data: DataTableValue[]) => void;
};
const ItemsTable = <T extends DataTableValue>({
  columns,
  data,
  onToolbarBtnClick = () => {},
  searchFields = [],
  toolbarBtnClassName = "",
  toolbarBtnLabel,
  loading,
  showToolbar = true,
  showToolbarBtn = true,
  tableName,
  setFilteredData,
}: ItemsTableProps<T>) => {
  const tz = useTimezone();
  const { userSettings, project } = useProjectContext();
  const [pageLoaded, setPageLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [filterItems, setFilterItems] = useState<T[]>(data);
  const [excelData, setExcelData] = useState<DataTableValue[]>([]);
  const tableDetails = useUserTableDetails(userSettings, tableName);
  const dt = useRef<DataTable<DataTableValueArray>>(null);
  const [customColumns, setCustomColumns, formattedColumns, mapCols, formatObjectFields, handleColumnSave, updateLoading] =
    useCustomColumns(tableName, columns, filterItems, tableDetails?.rows || 10);

  const [updateTableDetails] = useUpdateTableDetailsForUserMutation();

  useEffect(() => {
    if (formatObjectFields) {
      setExcelData(formatObjectFields);
      dt.current?.reset();
    }
  }, [customColumns, formatObjectFields]);

  const exportCSV = () => {
    dt.current?.exportCSV({ selectionOnly: false });
  };

  const handleExportExcel = () => {
    const tableData = excelData.map(row => {
      const newData: any = {};

      formattedColumns.forEach(col => {
        if (col.hideInFile || col.hidden) return;
        newData[col.header] = col.field
          ? fmtDatetime(findObjectValue(row, col.field)?.toString(), tz.display, DATE_FORMATTER_MINUTES)
          : col.body;
      });
      return newData;
    });
    exportExcel(tableData, tableName);
  };

  const onPage = (e: DataTableStateEvent) => {
    const tableDetailsInput: TablesDetailsInput = {
      table: tableName,
      rows: e.rows,
      columnOrders: tableDetails?.columnOrders || [],
    };
    setPage(e.first);
    if (pageLoaded && tableDetails?.rows !== e.rows) {
      updateTableDetails({
        variables: { projectId: project?.mdmProjectById.id!, tableDetails: tableDetailsInput },
      });
    }
    setPageLoaded(true);
  };

  return (
    <>
      {showToolbar && (
        <TableToolbar
          searchData={data}
          onSearch={setFilterItems}
          onClickBtn={onToolbarBtnClick}
          searchFields={searchFields}
          classNameBtn={toolbarBtnClassName}
          showBtn={showToolbarBtn}
          labelBtn={toolbarBtnLabel}
          additionalControls={
            <>
              <Button
                type="button"
                icon="pi pi-file"
                size="small"
                className="p-button-outlined p-button-warning"
                title={T("action.export-to-csv")}
                onClick={exportCSV}
              />
              <Button
                type="button"
                icon="pi pi-file-excel"
                size="small"
                className="p-button-outlined p-button-warning"
                title={T("action.export-to-excel")}
                onClick={handleExportExcel}
              />
              <TableColumnSelection
                customColumns={customColumns}
                tableName={tableName}
                setCustomColumns={setCustomColumns}
                handleColumnSave={handleColumnSave}
                mapCol={mapCols}
                loading={updateLoading}
              />
            </>
          }
        />
      )}
      <DataTable
        ref={dt}
        value={formatObjectFields}
        onValueChange={tableData => {
          setExcelData(tableData);
          setFilteredData?.(tableData);
        }}
        stripedRows
        paginator
        rows={tableDetails?.rows || 10}
        reorderableColumns
        onColReorder={e => setCustomColumns(prev => moveItem(prev, e.dragIndex, e.dropIndex))}
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate={T("table.page-report-template", {
          first: "{first}",
          last: "{last}",
          totalRecords: "{totalRecords}",
        })}
        loading={loading}
        size="small"
        className="overflow-x-auto"
        first={page}
        onPage={onPage}
      >
        {formattedColumns.map(col => (
          <Column key={col.header} {...col} />
        ))}
      </DataTable>
    </>
  );
};

export default ItemsTable;
